export interface User {
  id: string
  email: string
  name?: string
  first_name?: string
  last_name?: string
  phone?: string
  role: 'consumer' | 'business' | 'admin'
  isVerified?: boolean
  is_active?: boolean
  lastLogin?: string
  createdAt?: string
  updatedAt?: string
  created_at?: string
  updated_at?: string
  preferences?: {
    notifications?: any
    privacy?: any
    [key: string]: any
  }
}

export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

export interface RegisterData {
  email: string
  password: string
  confirmPassword: string
  first_name: string
  last_name: string
  phone?: string
  role?: 'consumer' | 'business'
  acceptTerms: boolean
  address?: string
  acceptMarketing?: boolean
}

export interface ProfileUpdate {
  name?: string
  phone?: string
  email?: string
}

export interface AuthResponse {
  success: boolean
  user?: User | null
  token?: string | null
  refreshToken?: string | null
  message?: string
  error?: string
  pendingApproval?: boolean
}

export interface PasswordResetRequest {
  email: string
}

export interface PasswordResetData {
  token: string
  password: string
  confirmPassword: string
}

class AuthService {
  private baseUrl = (import.meta.env.VITE_API_URL as string) || ''
  private token: string | null = null
  private refreshToken: string | null = null
  private user: User | null = null

  constructor() {
    this.loadFromStorage()
  }

  private loadFromStorage(): void {
    try {
      this.token = localStorage.getItem('auth_token')
      this.refreshToken = localStorage.getItem('auth_refresh_token')
      const userStr = localStorage.getItem('auth_user')
      if (userStr) {
        this.user = JSON.parse(userStr)
      }
    } catch (error) {
      console.error('Error loading auth from storage:', error)
      this.clearStorage()
    }
  }

  private saveToStorage(): void {
    try {
      if (this.token) {
        localStorage.setItem('auth_token', this.token)
      }
      if (this.refreshToken) {
        localStorage.setItem('auth_refresh_token', this.refreshToken)
      }
      if (this.user) {
        localStorage.setItem('auth_user', JSON.stringify(this.user))
      }
    } catch (error) {
      console.error('Error saving auth to storage:', error)
    }
  }

  private clearStorage(): void {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_refresh_token')
    localStorage.removeItem('auth_user')
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log('🔐 === FRONTEND LOGIN DEBUG START ===')
      console.log('🔐 Base URL:', this.baseUrl)
      console.log('🔐 Login credentials:', {
        email: credentials.email,
        password: credentials.password ? '[HIDDEN]' : '[MISSING]',
        rememberMe: credentials.rememberMe
      })
      console.log('🔐 Request URL:', `${this.baseUrl}/api/auth/login`)
      
      const requestBody = JSON.stringify(credentials)
      console.log('🔐 Request body:', requestBody)
      console.log('🔐 Request body length:', requestBody.length)
      
      const response = await fetch(`${this.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody,
      })

      console.log('🔐 Response status:', response?.status)
      console.log('🔐 Response statusText:', response?.statusText)
      try {
        // Some test mocks may not provide a real Headers object
        const headersObj = (response && (response as any).headers && typeof (response as any).headers.entries === 'function')
          ? Object.fromEntries((response as any).headers.entries())
          : {}
        console.log('🔐 Response headers:', headersObj)
      } catch (e) {
        console.log('🔐 Response headers: [unavailable]')
      }
      console.log('🔐 Response ok:', response?.ok)

      let rawText = await response.text()
      const origLength = rawText.length
      // Try trimmed in case of trailing/leading whitespace or BOM
      const trimmed = rawText.trim()
      if (trimmed.length !== rawText.length) {
        rawText = trimmed
        console.log('🔐 Response body was trimmed:', { origLength, newLength: rawText.length })
      }
      let data: { success?: boolean; token?: string; refreshToken?: string; user?: unknown; message?: string }
      try {
        data = rawText ? JSON.parse(rawText) : {}
      } catch (parseError) {
        const err = parseError as SyntaxError
        const match = err.message.match(/position (\d+)/)
        const pos = match ? parseInt(match[1], 10) : -1
        console.error('🔐 Response body is not valid JSON:', parseError)
        console.error('🔐 Response body length:', rawText.length)
        if (pos >= 0) {
          const start = Math.max(0, pos - 30)
          const slice = rawText.slice(start, pos + 30)
          console.error('🔐 Around error position', pos, ':', JSON.stringify(slice))
          console.error('🔐 Char codes at position:', pos < rawText.length ? rawText.charCodeAt(pos) : 'N/A')
        }
        console.error('🔐 Last 80 chars:', JSON.stringify(rawText.slice(-80)))
        return {
          success: false,
          error: 'Invalid response from server. Please try again.'
        }
      }
      console.log('🔐 Response data:', data)

      if (!response.ok) {
        console.log('🔐 Login failed - Response not ok')
        console.log('🔐 Error message:', data.message)
        console.log('🔐 Full error data:', data)
        return {
          success: false,
          error: data.message || 'Login failed'
        }
      }

      console.log('🔐 Login successful - Setting tokens')
      this.token = data.token ?? null
      this.refreshToken = data.refreshToken ?? null
      this.user = (data.user as User) ?? null

      console.log('🔐 Tokens set:', {
        token: this.token ? '[SET]' : '[MISSING]',
        refreshToken: this.refreshToken ? '[SET]' : '[MISSING]',
        user: this.user ? '[SET]' : '[MISSING]'
      })

      this.saveToStorage()
      console.log('🔐 Data saved to storage')

      console.log('🔐 === FRONTEND LOGIN DEBUG END - SUCCESS ===')
      return {
        success: true,
        user: this.user,
        token: this.token,
        refreshToken: this.refreshToken,
        message: 'Login successful'
      }
    } catch (error) {
      console.log('🔐 === FRONTEND LOGIN DEBUG END - ERROR ===')
      console.error('🔐 Login error:', error)
      console.error('🔐 Error type:', typeof error)
      console.error('🔐 Error message:', error instanceof Error ? error.message : 'Unknown error')
      console.error('🔐 Error stack:', error instanceof Error ? error.stack : 'No stack trace')
      return {
        success: false,
        error: 'Network error occurred'
      }
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: result.message || 'Registration failed'
        }
      }

      if (result.pendingApproval) {
        return {
          success: true,
          user: result.user,
          pendingApproval: true,
          message: result.message || 'Registration successful - pending admin approval'
        }
      }

      this.token = result.token
      this.refreshToken = result.refreshToken
      this.user = result.user

      this.saveToStorage()

      return {
        success: true,
        user: this.user,
        token: this.token,
        refreshToken: this.refreshToken,
        message: 'Registration successful'
      }
    } catch (error) {
      console.error('Registration error:', error)
      return {
        success: false,
        error: 'Network error occurred'
      }
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.token) {
        await fetch(`${this.baseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
        })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.clearAuth()
    }
  }

  async updateProfile(updates: ProfileUpdate): Promise<User> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/profile`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Profile update failed')
      }

      this.user = data.user
      this.saveToStorage()

      if (!this.user) throw new Error('Profile update did not return user')
      return this.user
    } catch (error) {
      console.error('Profile update error:', error)
      throw error
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/change-password`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Password change failed')
      }
    } catch (error) {
      console.error('Password change error:', error)
      throw error
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Password reset request failed')
      }
    } catch (error) {
      console.error('Password reset request error:', error)
      throw error
    }
  }

  async resetPassword(data: PasswordResetData): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Password reset failed')
      }
    } catch (error) {
      console.error('Password reset error:', error)
      throw error
    }
  }

  async verifyEmail(token: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Email verification failed')
      }
    } catch (error) {
      console.error('Email verification error:', error)
      throw error
    }
  }

  async refreshAuth(): Promise<AuthResponse> {
    if (!this.refreshToken) {
      return {
        success: false,
        error: 'No refresh token available'
      }
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      })

      const data = await response.json()

      if (!response.ok) {
        this.clearAuth()
        return {
          success: false,
          error: data.message || 'Token refresh failed'
        }
      }

      this.token = data.token
      this.refreshToken = data.refreshToken
      this.user = data.user

      this.saveToStorage()

      return {
        success: true,
        user: this.user,
        token: this.token,
        refreshToken: this.refreshToken,
        message: 'Token refreshed successfully'
      }
    } catch (error) {
      console.error('Token refresh error:', error)
      this.clearAuth()
      return {
        success: false,
        error: 'Network error occurred'
      }
    }
  }

  async verifyToken(): Promise<boolean> {
    if (!this.token) {
      return false
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/verify`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })

      if (response.ok) {
        const data = await response.json()
        this.user = data.user
        this.saveToStorage()
        return true
      } else {
        this.clearAuth()
        return false
      }
    } catch (error) {
      console.error('Token verification error:', error)
      this.clearAuth()
      return false
    }
  }

  async getCurrentUser(): Promise<User | null> {
    if (!this.token) {
      return null
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/me`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })

      if (response.ok) {
        const data = await response.json()
        this.user = data.user
        this.saveToStorage()
        return this.user
      } else {
        // Don't clear auth here - let the caller decide (e.g., try refresh first)
        return null
      }
    } catch (error) {
      console.error('Get current user error:', error)
      // Don't clear auth here - let the caller decide
      return null
    }
  }

  clearAuth(): void {
    this.token = null
    this.refreshToken = null
    this.user = null
    this.clearStorage()
  }

  getAuthHeaders(): Record<string, string> {
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    }
  }

  getToken(): string | null {
    return this.token
  }

  getRefreshToken(): string | null {
    return this.refreshToken
  }

  getUser(): User | null {
    return this.user || null
  }

  isAuthenticated(): boolean {
    return !!this.token && !!this.user
  }

  hasRole(role: string): boolean {
    return this.user?.role === role
  }

  isAdmin(): boolean {
    return this.user?.role === 'admin'
  }

  isBusiness(): boolean {
    return this.user?.role === 'business'
  }

  isConsumer(): boolean {
    return this.user?.role === 'consumer'
  }
}

export const authService = new AuthService()
export default authService

